<template>
  <div
    :class="['rich-inbox-button-wrapper', { 'rich-inbox-button-wrapper-animate': shouldAnimate }]"
  >
    <LcButton
      :aria-label="$t('richinbox.button.arialabel')"
      :count="unreadMessagesCount"
      size="small"
      type="button"
      variant="raised"
      class="rich-inbox-button"
      no-padding
      @click="isBottomSheetOpen = true"
    >
      <img
        alt=""
        :class="{ 'bell-shake': !hasAnimated }"
        :height="ICON_SIZE"
        :src="bellIcon"
        :width="ICON_SIZE"
        aria-hidden="true"
      />
    </LcButton>

    <LcBottomSheet
      class="bottom-sheet"
      v-model="isBottomSheetOpen"
      v-bind="{ snapHeights, ignoreOutsideClickOn }"
    >
      <template #title>
        <div class="flex items-center gap-050">
          <h2 class="headline-h5-headline">{{ $t('richinbox.headline') }}</h2>
          <LcCountBubble
            v-if="unreadMessagesCount"
            :class="$style['unread-count']"
            :count="unreadMessagesCount"
          />
        </div>
      </template>

      <template v-if="unreadMessagesCount > 0" #actions>
        <LcMenuButton
          ref="menuButtonRef"
          v-bind="{ options }"
          icon="more-horizontal"
          :z-index="9999"
          placement="bottom-end"
        />
      </template>

      <div v-if="isLoading" class="flex flex-col gap-100 pt-050">
        <LcSkeleton v-for="i in 2" :key="i" height="7.2rem" radius="medium" />
      </div>

      <RichInboxErrorState v-else-if="isErrorState" @retry="handleRetry" />

      <RichInboxEmptyState v-else-if="messages.length <= 0" />

      <TransitionGroup v-else tag="ul" name="richinbox-message" class="flex flex-col pt-050">
        <li v-for="message in messages" :key="message.id" class="message-list-item">
          <div class="message-list-item-inner">
            <RichInboxMessageCard
              v-bind="message"
              @delete="setDeletedMessage"
              @read="setReadMessage"
            />
          </div>
        </li>
      </TransitionGroup>
    </LcBottomSheet>
  </div>
</template>

<script lang="ts" setup>
import { LcBottomSheet, type LcMenuButtonOption } from '@lottocom/frontend-components'
import { useTimeoutFn } from '@vueuse/core'

import bellStatic from '../assets/img/bell-static.svg'

const ICON_SIZE = 24
const ANIMATION_DURATION = 2000

const {
  syncReadAndDeletedMessages,
  fetchMessages,
  messages,
  setDeletedMessage,
  setReadMessage,
  state,
  unreadMessagesCount,
  setReadMessageAll,
  isLoading,
} = useOptimove()

const { t } = useI18n()

const isBottomSheetOpen = ref(false)

const menuButton = useTemplateRef('menuButtonRef')

const expandedMessageId = ref<Maybe<string>>(null)
const hasAnimated = useState('richInboxHasAnimated', () => false)
const bellIcon = bellStatic

const options = computed<LcMenuButtonOption[]>(() => [
  {
    icon: 'mark-all-as-read',
    label: t('richinbox.menu.readall'),
    onClick: async () => {
      console.log('hello')
      setReadMessageAll()
      await syncReadAndDeletedMessages()
    },
  },
])

const ignoreOutsideClickOn = () => {
  const menu = menuButton.value?.menuRef
  return menu ? [menu] : []
}

const isErrorState = computed(() => state.value === 'error')

const snapHeights = computed(() => {
  if (isErrorState.value || (isErrorState.value && isLoading.value)) return ['26rem']
  if (messages.value.length <= 0 || isLoading.value) return ['20rem']
  return ['95dvh']
})

const shouldAnimate = computed(() => !hasAnimated.value)

const { resume: startRetry, pause: stopRetry } = useIntervalFn(fetchMessages, 10_000)

const handleRetry = async () => {
  stopRetry()
  await fetchMessages()
  if (isErrorState.value) startRetry()
}

useTimeoutFn(
  () => {
    hasAnimated.value = true
  },
  ANIMATION_DURATION,
  { immediate: !hasAnimated.value },
)

watch(isBottomSheetOpen, async (isBottomSheetOpen) => {
  if (isBottomSheetOpen) return
  await syncReadAndDeletedMessages()
})

watchImmediate(isErrorState, (nextIsErrorState) => {
  if (nextIsErrorState) startRetry()
  else stopRetry()
})

onMounted(fetchMessages)

provide(richInboxExpandedIdKey, expandedMessageId)
</script>

<style lang="scss" scoped>
.rich-inbox-button-wrapper {
  width: 2rem;
  line-height: 1;

  .rich-inbox-button {
    padding: token('size/space/gap/050');
    border: none;
    transition: none;

    /* stylelint-disable-next-line selector-class-pattern */
    :deep(.lc-button__count-text) {
      position: absolute;
      top: -0.475rem;
      right: -0.475rem;
      flex-shrink: 0;
      margin: 0;
      background-color: token('color/background/accent/danger/danger-bold');
    }
  }

  &-animate {
    animation: fade-in 200ms ease-out both;
  }
}

.message-list-item {
  display: grid;
  grid-template-rows: 1fr;
}

.message-list-item-inner {
  min-height: 0;
  padding-bottom: token('size/space/gap/100');
}

.richinbox-message-leave-active {
  overflow: hidden;
  transition:
    transform 250ms ease-in,
    opacity 250ms ease-in,
    grid-template-rows $transition-duration-md $bezier-expressive-enter 250ms;
}

.richinbox-message-leave-to {
  grid-template-rows: 0fr;
  opacity: 0;
  transform: translateX(-100%);
}

@keyframes fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes bell-shake {
  0% {
    transform: rotate(0deg);
  }

  10% {
    transform: rotate(-14deg);
  }

  20% {
    transform: rotate(12deg);
  }

  30% {
    transform: rotate(-10deg);
  }

  40% {
    transform: rotate(8deg);
  }

  50% {
    transform: rotate(-6deg);
  }

  60% {
    transform: rotate(4deg);
  }

  70% {
    transform: rotate(-2deg);
  }

  80%,
  100% {
    transform: rotate(0deg);
  }
}

.bell-shake {
  transform-origin: center top;
  animation: bell-shake 1000ms ease-in-out 800ms 1 both;
}

@media (prefers-reduced-motion: reduce) {
  .rich-inbox-button-wrapper-animate,
  .bell-shake {
    animation: none;
  }

  .richinbox-message-leave-active {
    transition: none;
  }
}
</style>

<style lang="scss" module>
.unread-count {
  position: relative;
  background-color: token('color/background/accent/danger/danger-bold');
}
</style>
